import { z } from "zod";

import type { NormalizedIssue, NormalizedRemedy, NormalizedComment } from "@common/types/issue";
import { cardParagraphAnchorSchema, redlineTextProposalSchema } from "@common/utils/redline/wire/redline-payload";

export const ImportanceSchema = z.enum(["High", "Medium", "Low", "Draft"]);

export const IssueStageSchema = z.enum([
  "queued",
  "detection",
  "in-progress",
  "remedy",
  "done",
  "error",
  "skipped",
]);

export const NormalizedRemedySchema = z.object({
  id: z.string(),
  label: z.string(),
  isPrimary: z.boolean(),
  ooxmlAfter: z.array(z.string()).optional(),
  // Proposal engine only. The core drops None fields from its payload, so a
  // markup remedy arrives with no key; the default reads that as null, so in
  // memory the field is required and never undefined.
  textRedline: redlineTextProposalSchema.nullable().default(null),
  targetBookmarks: z.array(z.string()),
  trackedChanges: z.array(
    z.object({
      tag: z.enum(["del", "ins"]),
      text: z.string(),
    }),
  ),
  applied: z.boolean(),
  diffCount: z.object({
    added: z.number(),
    removed: z.number(),
  }),
});

export const CommentPlacementSchema = z.enum(["first_word", "span", "after_paragraph"]);

export const NormalizedCommentSchema = z.object({
  id: z.string(),
  text: z.string(),
  bookmark: z.string().optional(),
  placement: CommentPlacementSchema.nullable().default(null),
  anchorText: z.string().nullable().default(null),
  paragraphText: z.string().nullish(),
  paragraphAnchor: cardParagraphAnchorSchema.nullish(),
  applied: z.boolean(),
});

export const NormalizedIssueSchema = z.object({
  ruleUuid: z.string().optional(),
  index: z.number(),
  name: z.string(),
  description: z.string().optional(),
  importance: ImportanceSchema,
  match: z.boolean(),
  stage: IssueStageSchema,
  responded: z.boolean(),
  conceded: z.boolean(),
  reasoning: z.string().optional(),
  error: z.string().optional(),
  detectionBookmarks: z.array(z.string()).optional(),
  remedies: z.array(z.unknown()).transform(remediesThatParse).optional(),
  comments: z.array(NormalizedCommentSchema).optional(),
});

function remediesThatParse(rawRemedies: unknown[]): z.infer<typeof NormalizedRemedySchema>[] {
  return rawRemedies.flatMap((rawRemedy) => {
    const remedy = NormalizedRemedySchema.safeParse(rawRemedy);
    return remedy.success ? [remedy.data] : [];
  });
}

function issuesThatParse(rawIssues: unknown[]): z.infer<typeof NormalizedIssueSchema>[] {
  return rawIssues.flatMap((rawIssue) => {
    const issue = NormalizedIssueSchema.safeParse(rawIssue);
    return issue.success ? [issue.data] : [];
  });
}

export const LiveReviewDataSchema = z.object({
  issues: z.array(z.unknown()).transform(issuesThatParse),
  status: z.enum(["running", "completed", "failed"]),
  sourceDocRef: z.string(),
  generatedAt: z.string(),
  runStartedAt: z.iso.datetime({ offset: true }).nullish(),
  playbookName: z.string().nullish(),
  reviewId: z.string().nullish(),
});

export type LiveReviewData = z.infer<typeof LiveReviewDataSchema>;

/**
 * The message shape `parseRunningLiveReview` reads. Kept structural (rather than
 * importing an app's `ConversationMessage`) so both the add-in context and the
 * chat page can pass their message objects without a cross-package type dep.
 */
interface LiveReviewMessageCandidate {
  content?: string | null;
  toolName?: string;
  turnArtifacts?: { tool_name?: string } | null;
  completedAt?: string | null;
}

/**
 * Returns the parsed `LiveReviewData` when `msg` is a `live_review` message
 * whose payload is currently `running`, else `null`. This is the single source
 * of truth for "a playbook review is in flight" — it drives both the polling
 * `keepPolling` predicate and the sticky live-review status bar, so the bar's
 * lifetime always matches the polling lifetime.
 */
export function parseRunningLiveReview(
  msg: LiveReviewMessageCandidate,
): LiveReviewData | null {
  if (msg.completedAt) return null;
  const data = parseLiveReviewMessage(msg);
  return data && data.status === "running" ? data : null;
}

export function parseLiveReviewMessage(
  msg: LiveReviewMessageCandidate,
): LiveReviewData | null {
  const toolName = msg.toolName ?? msg.turnArtifacts?.tool_name;
  if (toolName !== "live_review") return null;
  const trimmed = (msg.content ?? "").trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return null;
  try {
    const parsed = LiveReviewDataSchema.safeParse(JSON.parse(trimmed));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export const UNREVIEWED_ISSUE_STAGES: ReadonlySet<z.infer<typeof IssueStageSchema>> = new Set(["error", "skipped"]);

// Per-rule stages that count as "checked" (the rule is done being evaluated).
const TERMINAL_ISSUE_STAGES: ReadonlySet<z.infer<typeof IssueStageSchema>> = new Set([
  "done",
  ...UNREVIEWED_ISSUE_STAGES,
]);

/**
 * The rule rows worth showing to the user: drop the internal `__init__` /
 * `__final__` sentinels and any nameless placeholder. Shared by the inline
 * LiveReviewWidget and the sticky status bar so both count/list the same set.
 */
export function getVisibleLiveReviewIssues(data: LiveReviewData): NormalizedIssue[] {
  return data.issues.filter(
    (i) =>
      !!(i.name && i.name.trim()) &&
      i.ruleUuid !== "__init__" &&
      i.ruleUuid !== "__final__",
  );
}

/** Live progress over the visible rules: how many have reached a terminal stage. */
export function getLiveReviewRuleProgress(data: LiveReviewData): {
  checked: number;
  total: number;
} {
  const visible = getVisibleLiveReviewIssues(data);
  return {
    total: visible.length,
    checked: visible.filter((i) => TERMINAL_ISSUE_STAGES.has(i.stage)).length,
  };
}

type AssertEqual<A, B> =
  (<T>() => T extends A ? 1 : 2) extends (<T>() => T extends B ? 1 : 2)
    ? true
    : never;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertRemedyParity: AssertEqual<
  z.infer<typeof NormalizedRemedySchema>,
  NormalizedRemedy
> = true;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertCommentParity: AssertEqual<
  z.infer<typeof NormalizedCommentSchema>,
  NormalizedComment
> = true;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertIssueParity: AssertEqual<
  z.infer<typeof NormalizedIssueSchema>,
  NormalizedIssue
> = true;
